import * as React from 'react'
import { cn } from '@/lib/cn'

export type Tone = 'live' | 'ok' | 'info' | 'warn' | 'danger' | 'neutral' | 'accent'

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: Tone
  /** Leading solid dot — a second, non-colour cue for status. */
  dot?: boolean
  /** Leading animated dot. Reserved for genuinely live things. */
  pulse?: boolean
  /** Monospace, mixed case, no tracking. For IDs and codes. */
  mono?: boolean
}

/**
 * Tint plus text colour, and nothing else.
 *
 * The previous badge also carried a hairline border, which meant every status
 * cell in a 600-row table drew an extra rectangle. At table scale that reads as
 * noise, and the tint alone is already a stronger signal than the outline was.
 * `rounded-md` rather than a full pill for the same reason: the badge should
 * belong to the same family as the buttons and inputs around it.
 */
const TONE: Record<Tone, string> = {
  live: 'bg-live-soft text-live',
  ok: 'bg-ok-soft text-ok',
  info: 'bg-info-soft text-info',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  neutral: 'bg-neutral-soft text-muted',
  accent: 'bg-accent-soft text-accent',
}

export function Badge({
  tone = 'neutral',
  dot = false,
  pulse = false,
  mono = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  const showDot = dot || pulse

  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-md px-2 py-0.5',
        'text-[12px] leading-[1.35] font-medium',
        // Uppercase is a status treatment — it makes a short state word scan as
        // a label. An id rendered in caps is just harder to read, so `mono`
        // opts out of the casing and the tracking together.
        mono ? 'font-mono tracking-normal normal-case' : 'tracking-[0.04em] uppercase',
        TONE[tone],
        className
      )}
      {...rest}
    >
      {showDot ? (
        pulse ? (
          <span className="pulse-dot" aria-hidden="true" />
        ) : (
          <span className="size-[6px] flex-none rounded-full bg-current" aria-hidden="true" />
        )
      ) : null}
      <span className="truncate">{children}</span>
    </span>
  )
}
